import { useAtomValue } from 'jotai'
import { useEffect, useState } from 'react'
import { useLoaderData, useNavigate } from 'react-router-dom'

import { validateOTP } from 'client/one-stop-checkin/one-stop-checkin.client'
import { registerMissingRequest } from 'client/stock-transfers/stock-transfers.client'
import { useRequiredParams } from 'hooks/params.hook'
import { ProductQuantitiesSchema } from 'types/assembly/model'
import type { RegisterMissingData } from 'types/utils/model'
import {
  MEASURE_LABELS_PLURAL,
  MISSING_LABELS_PLURAL,
  ZOD_ERROR
} from 'utils/constants'
import { toastError, toastSuccess } from 'utils/toast'
import { selectedContainersAtom, selectedProductAtom } from '../Assemby.state'
import { useGoToProducts } from '../products/Products.navigator'

export const useMissing = () => {
  const navigate = useNavigate()
  const product = useAtomValue(selectedProductAtom)
  const selectedContainers = useAtomValue(selectedContainersAtom)
  const [quantity, setQuantity] = useState('')
  const [loading, setLoading] = useState(false)
  const [showOTPModal, setShowOTPModal] = useState(false)
  const { missingType, transferId, docNum } = useRequiredParams(
    'missingType',
    'transferId',
    'docNum'
  )
  const productsPage = useGoToProducts()
  const rawQuantities = useLoaderData()

  useEffect(() => {
    const result = ProductQuantitiesSchema.safeParse(rawQuantities)
    if (!result.success) {
      throw Error(ZOD_ERROR)
    }

    const currentQuantity =
      missingType === 'waste'
        ? result.data.wasteQuantity
        : result.data.missingQuantity
    if (currentQuantity) {
      setQuantity(String(currentQuantity))
    }
  }, [missingType, rawQuantities])

  const result = ProductQuantitiesSchema.safeParse(rawQuantities)
  if (!result.success) {
    throw Error(ZOD_ERROR)
  }

  const onGoBack = () => {
    navigate(-1)
  }

  const onChangeQuantity = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuantity(e.target.value)
  }

  const confirmQuantity = async () => {
    if (!quantity || +quantity <= 0) {
      toastError('Ingrese una cantidad valida.')
      return
    }

    const missingOrWasteQuantity =
      missingType === 'waste'
        ? result.data.missingQuantity
        : result.data.wasteQuantity
    const totalRegistered =
      result.data.quantity?.reduce((acc, curr) => acc + curr, 0) || 0
    const globalTotal =
      totalRegistered + +quantity + (missingOrWasteQuantity || 0)
    if (product && +globalTotal > product.quantity) {
      toastError(
        'La cantidad ingresada es mayor a la solicitada, intente de nuevo.'
      )
      return
    }

    setShowOTPModal(true)
  }

  const closeOtpModal = () => {
    setShowOTPModal(false)
  }

  const registerMissing = async () => {
    if (!product) {
      return
    }

    const missingData: RegisterMissingData = {
      transferId,
      ean: product.barcode,
      quantity: +quantity,
      type: missingType,
      containerId: product.containerId
    }
    try {
      setLoading(true)
      await registerMissingRequest(missingData)
      toastSuccess(
        `Se han registrado como ${MISSING_LABELS_PLURAL[missingType]} ${quantity} ${MEASURE_LABELS_PLURAL[product.measureUnit]} de ${product.name}`
      )
      productsPage.go(transferId, docNum, selectedContainers)
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const onConfirmOTPCode = async (code: string) => {
    try {
      setLoading(true)
      await validateOTP(code, 'OTP_TYPE_ARMADO')
      registerMissing()
      setShowOTPModal(false)
    } catch (error) {
      console.error(error)
      toastError('El código ya no es válido, solicita otro a tu supervisor.')
      setLoading(false)
    }
  }

  return {
    product,
    quantity,
    loading,
    showOTPModal,
    actions: {
      onGoBack,
      onChangeQuantity,
      confirmQuantity,
      closeOtpModal,
      onConfirmOTPCode
    }
  }
}
